import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { UploadCloud, Loader2 } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { API_URL } from "../config";
import { toast } from "sonner";

const inputCls =
  "w-full rounded-lg border border-[#E5E5E5] bg-white px-4 py-3 text-base text-[#1C1C1C] placeholder:text-[#A3A3A3] focus:outline-none focus:ring-2 focus:ring-[#C87A38] focus:border-transparent transition-shadow";

const Field = ({ label, htmlFor, required, children }) => (
  <div>
    <label htmlFor={htmlFor} className="block text-sm font-semibold text-[#4A3018] mb-1.5">
      {label} {required && <span className="text-[#C87A38]">*</span>}
    </label>
    {children}
  </div>
);

export default function Quote() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [colours, setColours] = useState([]);
  const [sending, setSending] = useState(false);
  const [files, setFiles] = useState([]);
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    suburb: "",
    service: "Supply and Install",
    area: "",
    existing_floor: "",
    colour: searchParams.get("colour") || "",
    install_date: "",
    message: "",
  });

  useEffect(() => {
    axios.get(`${API_URL}/colours`).then((r) => setColours(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const c = searchParams.get("colour");
    if (c) setForm((f) => ({ ...f, colour: c }));
  }, [searchParams]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      files.forEach((f) => data.append("photos", f));
      await axios.post(`${API_URL}/quotes`, data);
      navigate("/thank-you");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong sending your enquiry. Please try again or give us a call.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="quote-page">
      <PageHeader
        label="Get a Free Quote"
        title="Request your free flooring quote"
        description="Fill in the details below and we will come back to you with a clear, written quote. Free quotes across Melbourne and surrounding suburbs."
        testId="quote-header"
      />

      <section className="py-20" data-testid="quote-form-section">
        <div className="max-w-3xl mx-auto px-6">
          <form onSubmit={submit} className="space-y-6" data-testid="quote-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Field label="Full name" htmlFor="full_name" required>
                <input id="full_name" className={inputCls} value={form.full_name} onChange={set("full_name")} required data-testid="quote-input-name" />
              </Field>
              <Field label="Phone number" htmlFor="phone" required>
                <input id="phone" type="tel" className={inputCls} value={form.phone} onChange={set("phone")} required data-testid="quote-input-phone" />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Field label="Email address" htmlFor="email" required>
                <input id="email" type="email" className={inputCls} value={form.email} onChange={set("email")} required data-testid="quote-input-email" />
              </Field>
              <Field label="Property address or suburb" htmlFor="suburb" required>
                <input id="suburb" className={inputCls} value={form.suburb} onChange={set("suburb")} required placeholder="e.g. Craigieburn, VIC" data-testid="quote-input-suburb" />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Field label="Required service" htmlFor="service" required>
                <select id="service" className={inputCls} value={form.service} onChange={set("service")} required data-testid="quote-select-service">
                  <option>Supply and Install</option>
                  <option>Supply Only</option>
                  <option>Installation Only</option>
                </select>
              </Field>
              <Field label="Approximate floor area (m²)" htmlFor="area">
                <input id="area" type="number" min="0" className={inputCls} value={form.area} onChange={set("area")} placeholder="e.g. 60" data-testid="quote-input-area" />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Field label="Existing floor type" htmlFor="existing_floor">
                <input id="existing_floor" className={inputCls} value={form.existing_floor} onChange={set("existing_floor")} placeholder="e.g. carpet, tiles, timber" data-testid="quote-input-existing-floor" />
              </Field>
              <Field label="Preferred flooring colour" htmlFor="colour">
                <select id="colour" className={inputCls} value={form.colour} onChange={set("colour")} data-testid="quote-select-colour">
                  <option value="">Not sure yet</option>
                  <optgroup label="Straight Lay">
                    {colours.filter((c) => (c.style || "straight") === "straight").map((c) => (
                      <option key={c.id} value={c.name}>{c.name} ({c.code})</option>
                    ))}
                  </optgroup>
                  {colours.some((c) => c.style === "herringbone") && (
                    <optgroup label="Herringbone">
                      {colours.filter((c) => c.style === "herringbone").map((c) => (
                        <option key={c.id} value={`${c.name} (Herringbone)`}>{c.name} — Herringbone ({c.code})</option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </Field>
            </div>
            <Field label="Required installation date" htmlFor="install_date">
              <input id="install_date" type="date" className={inputCls} value={form.install_date} onChange={set("install_date")} data-testid="quote-input-date" />
            </Field>
            <Field label="Message or additional information" htmlFor="message">
              <textarea id="message" rows={4} className={inputCls} value={form.message} onChange={set("message")} placeholder="Anything else we should know — stairs, furniture, access, timing…" data-testid="quote-input-message" />
            </Field>
            <Field label="Upload photos or floor plan">
              <label
                htmlFor="photos"
                className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-[#E5E5E5] bg-[#FAFAFA] px-4 py-8 cursor-pointer transition-colors hover:border-[#C87A38]"
                data-testid="quote-upload-zone"
              >
                <UploadCloud className="w-8 h-8 text-[#C87A38]" />
                <span className="text-sm font-medium text-[#525252]">
                  {files.length > 0 ? `${files.length} file${files.length > 1 ? "s" : ""} selected` : "Tap to add photos of your floors or a floor plan"}
                </span>
                <span className="text-xs text-[#A3A3A3]">Up to 6 images or PDFs</span>
              </label>
              <input
                id="photos"
                type="file"
                multiple
                accept="image/*,.pdf"
                className="hidden"
                onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 6))}
                data-testid="quote-input-photos"
              />
            </Field>
            <button
              type="submit"
              disabled={sending}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C87A38] px-7 py-4 text-base font-semibold text-white transition-colors hover:bg-[#A6632B] disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-[#C87A38] focus:ring-offset-2"
              data-testid="quote-submit-button"
            >
              {sending && <Loader2 className="w-4 h-4 animate-spin" />}
              {sending ? "Sending…" : "Request My Free Quote"}
            </button>
            <p className="text-xs text-[#A3A3A3] text-center">
              We only use your details to respond to your flooring enquiry.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}
